import styles from './bulkConfirmPaymentsForm.module.css'
import { useNavigate } from "react-router-dom"
import { Badge, FieldType, FieldValue, Form, FormField, O, option, PanelWrapper, useGetCrumbs, useUIState } from "../../../../../features/ui"
import { RoutePath } from "../../../../../features/routes"
import { useEffect, useMemo, useState } from "react"
import { useSaleOperationLoaders } from "../../../loaders/saleOperationLoaders"
import { SaleOperation } from "../../../state/types"
import { saleOperationHelpers, useSaleOperationHooks } from "../../../hooks/saleOperationHooks"
import { firstItem } from "../../../../../utils/listUtils"
import { TableName } from "../../../../../tables"
import { operationHelpers } from "../../../../operations"
import { CashFund, PaymentCategory, PaymentType, PaymentTypeField, isSelectablePaymentType, useCashFundLoaders, usePaymentTypeLoaders } from '../../../../administration'
import { useActions } from './actions'
import { Data2 } from '../../../../../features/data-types'

export const BulkConfirmPaymentsForm = () => {
    const { bulkDataTypeIds } = useUIState()

    const saleOperationLoaders = useSaleOperationLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const cashFundLoaders = useCashFundLoaders()

    const paymentTypes = useMemo(() => paymentTypeLoaders().findAllPaymentTypes(true).filter(paymentType =>
        isSelectablePaymentType(paymentType) &&
        [PaymentCategory.CARD, PaymentCategory.CASH_FUND].includes(paymentType.category) && paymentType.cashFundIds.length > 0
    ), [])

    const [operations, setOperations] = useState<SaleOperation[]>([])
    const [paymentType, setPaymentType] = useState<O<PaymentType>>(firstItem(paymentTypes))
    const [cashFunds, setCashFunds] = useState<CashFund[]>([])
    const [cashFundId, setCashFundId] = useState<O<string>>()
    
    const { getTable } = saleOperationHelpers
    const { describeCode } = operationHelpers
    const { isPayable } = useSaleOperationHooks()
    const navigate = useNavigate()

    const { confirmPayments } = useActions()

    const load = async () => {
        const { loadSaleOperations} = saleOperationLoaders()
        const operations = await loadSaleOperations(bulkDataTypeIds)
        const suitableOperations = operations.filter(operation => isPayable(operation))
        const sortedOperations = suitableOperations.sort((a, b) => a.code - b.code)
        setOperations(sortedOperations)
    }

    useEffect(() => {
        load()
    }, [bulkDataTypeIds])

    useEffect(() => {
        const newCashFunds = paymentType?.cashFundIds.map(cashFundId => cashFundLoaders().findCashFund(cashFundId)!) || []
        setCashFunds(newCashFunds)
        setCashFundId(firstItem(newCashFunds)?.id)
    }, [paymentType])

    const crumbs = useGetCrumbs('Ventas', RoutePath.SALES, 'Confirmar Pagos')
    
    const table = useMemo(() => operations.length > 0 ? getTable(firstItem(operations)!) : undefined, [operations.length])
    const isSale = useMemo(() => table === TableName.SALE_ORDERS, [table])

    const onChangePaymentType = (value?: FieldValue) => setPaymentType(paymentTypes.find(paymentType => paymentType.id === value))

    const onChangeCashFund = (value?: FieldValue) => setCashFundId(value as O<string>)

    const fields: FormField[] = [
        {
            name: 'codes',
            label: `${isSale ? 'Ventas' : 'Acopios'} (${operations.length})`,
            type: FieldType.TEXT,
            render: () => (
                <div className={styles.codes}>
                    {operations.map(operation => (
                        <Badge key={operation.id} label={describeCode(operation)} />
                    ))}
                </div>
            )
        },
        {
            name: 'paymentDate',
            type: FieldType.DATE,
            label: 'Fecha de Cobro',
            defaultValue: new Date()
        },
        PaymentTypeField({
            defaultValue: paymentType?.id,
            paymentTypes,
            onChange: onChangePaymentType
        }),
        {
            name: 'cashFundId',
            type: FieldType.TABLE,
            table: TableName.CASH_FUNDS,
            label: 'Caja Destino',
            value: cashFundId,
            options: cashFunds.map(cashFund => option(cashFund.id, cashFund.name)),
            narrow: true,
            onChange: onChangeCashFund
        }
    ]

    const onCancel = () => navigate(-1)

    const onSubmit = (data: Data2) => {
        confirmPayments(table!, operations.map(operation => operation.id), paymentType!.id, cashFundId!, data.paymentDate! as Date)
    }

    return (
        <PanelWrapper
            title="Confirmar Pagos Masivamente"
            crumbs={crumbs}
        >
            <Form
                formId="bulk-confirm-payments-form"
                fields={fields}
                createLabel="Confirmar Pagos"
                onSubmit={onSubmit}
                onCancel={onCancel}
            />
        </PanelWrapper>
    )
}
